/* Custom CCC feature: dark mode switch.

   The inline script in layout/theme.liquid applies the saved mode to <html>
   before first paint; this module keeps localStorage, the <html> attributes and
   every <theme-toggle> instance in sync afterwards.

   html attributes:
     data-color-mode          'light' | 'dark' — explicit visitor choice (absent = auto)
     data-color-mode-active   'light' | 'dark' — resolved mode, drives icon + logo swap
     data-color-mode-default  'auto' | 'light' | 'dark' — merchant setting

   The toggle shows at every width (the mobile header seats it beside the
   burger), so a saved choice applies on phones as well as desktop.
*/

const STORAGE_KEY = 'ccc:color-mode';
/* Bump POLICY_VALUE when merchant default policy changes and old visitor
   overrides must be cleared once (shared Phaser + CCC). */
const POLICY_KEY = 'ccc:color-mode-policy';
const POLICY_VALUE = 'auto-v1-20260720';

const root = document.documentElement;
const darkMedia = window.matchMedia('(prefers-color-scheme: dark)');

function applyColorModePolicyReset() {
  try {
    if (localStorage.getItem(POLICY_KEY) === POLICY_VALUE) return;
    localStorage.removeItem(STORAGE_KEY);
    localStorage.setItem(POLICY_KEY, POLICY_VALUE);
  } catch {
    /* private browsing — no stored override to clear */
  }
}

function storedMode() {
  try {
    const mode = localStorage.getItem(STORAGE_KEY);
    return mode === 'light' || mode === 'dark' ? mode : null;
  } catch {
    return null;
  }
}

function autoMode() {
  const merchantDefault = root.getAttribute('data-color-mode-default') || 'auto';
  if (merchantDefault === 'light' || merchantDefault === 'dark') return merchantDefault;
  return darkMedia.matches ? 'dark' : 'light';
}

function activeMode() {
  return storedMode() || autoMode();
}

function syncToggles() {
  const stored = storedMode();
  if (stored) {
    root.setAttribute('data-color-mode', stored);
  } else {
    /* Drop explicit attribute so CSS auto (@media prefers-color-scheme) works
       when the visitor has not chosen an override. */
    root.removeAttribute('data-color-mode');
  }

  const active = stored || autoMode();
  const previous = root.getAttribute('data-color-mode-active');
  root.setAttribute('data-color-mode-active', active);
  if (previous && previous !== active) remountAccountPopovers();

  for (const toggle of document.querySelectorAll('theme-toggle button')) {
    toggle.setAttribute('aria-pressed', active === 'dark' ? 'true' : 'false');
    toggle.setAttribute('aria-label', active === 'dark' ? 'Switch to light mode' : 'Switch to dark mode');
  }

  syncColorSchemeMeta(active);
  syncThemeColorMetas(active);

  document.dispatchEvent(new CustomEvent('ccc:color-mode', { detail: { mode: active } }));
}

/* The account popover (<shopify-account>, with Shop's sign-in card inside it)
   reads the page background once, when it mounts, and keeps that theme for
   the life of the element: switch modes on the same page and the Shop card
   (recognised-user chip, disclaimer) stays light on the now-dark popover, or
   dark on light. Nothing in its API re-reads the colours, so re-create the
   element and let it mount again against the new mode. The clone carries the
   light-DOM slots and attributes; the component rebuilds its own shadow DOM.
   The boot script sets the active attribute pre-paint, so the first sync sees
   no change and the element is left alone on load. */
function remountAccountPopovers() {
  for (const account of document.querySelectorAll('shopify-account')) {
    account.replaceWith(account.cloneNode(true));
  }
}

/* Keep <meta name="color-scheme"> on the resolved mode only.
   Leaving content="light dark" lets Shop Pay follow OS dark preference and
   paint a white Visa chip slab on the purple wallet button (seen on light carts). */
function syncColorSchemeMeta(active) {
  const meta = document.querySelector('meta[name="color-scheme"]');
  if (!meta) return;
  meta.setAttribute('content', active === 'dark' ? 'dark' : 'light');
}

/* Browser UI bar (Android address bar / PWA chrome) follows the active mode.
   Single JS-owned meta — content always mirrors the resolved mode. Horizon's
   header.js also writes this meta from header-component's data-theme-color
   (a light-mode literal), so keep that dataset mode-correct too: whenever
   Horizon re-fires its writer it then writes the right colour. */
function syncThemeColorMetas(active) {
  const meta = document.querySelector('meta[data-ccc-theme-color]');
  if (!meta) return;
  const barColor = meta.getAttribute(active === 'dark' ? 'data-dark' : 'data-light');
  if (!barColor) return;
  meta.setAttribute('content', barColor);
  for (const header of document.querySelectorAll('header-component[data-theme-color]')) {
    if (active === 'dark') {
      if (!header.dataset.cccLightThemeColor) {
        header.dataset.cccLightThemeColor = header.dataset.themeColor;
      }
      header.dataset.themeColor = barColor;
    } else if (header.dataset.cccLightThemeColor) {
      header.dataset.themeColor = header.dataset.cccLightThemeColor;
    }
  }
}

function setExplicitMode(mode) {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* private browsing — mode still applies for this page */
  }
  root.setAttribute('data-color-mode', mode);
  syncToggles();
}

class ThemeToggle extends HTMLElement {
  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', () => {
      setExplicitMode(activeMode() === 'dark' ? 'light' : 'dark');
    });
    syncToggles();
  }
}

if (!customElements.get('theme-toggle')) {
  customElements.define('theme-toggle', ThemeToggle);
}

applyColorModePolicyReset();
syncToggles();

/* OS-level preference changed while in auto mode */
darkMedia.addEventListener('change', () => {
  if (!storedMode()) {
    syncToggles();
  }
});

/* Another tab switched mode */
window.addEventListener('storage', (event) => {
  if (event.key !== STORAGE_KEY && event.key !== POLICY_KEY) return;
  const mode = storedMode();
  if (mode) {
    root.setAttribute('data-color-mode', mode);
  } else {
    root.removeAttribute('data-color-mode');
  }
  syncToggles();
});
